import { useCallback, useEffect, useRef, useState } from 'react'

/** Breathing room below the pane so it never sits flush against the viewport. */
const DEFAULT_BOTTOM_GUTTER = 24

/** Floor, so a mis-measurement can never collapse the pane to nothing. */
const MIN_HEIGHT = 360

/**
 * Measures how much viewport is left below an element's top edge, so a pane can
 * fill exactly the remaining space and scroll internally.
 *
 * Why measure instead of `calc(100vh - <constant>)`: the chrome above this tab
 * (masthead, competition banner, tab bar) is not a fixed height — the banner
 * varies, and any of it can change without touching this feature. A hardcoded
 * offset silently drifts, leaving the pane either short or hanging past the
 * bottom of the screen.
 *
 * Why not `height: 100%`: that resolves only against an ancestor with a definite
 * height, and this tab renders inside the content-sized `Page` wrapper shared by
 * every schedule tab.
 *
 * Returns `undefined` until measured, so the caller can fall back rather than
 * flash at the wrong size.
 */
export const useFillViewportHeight = (
  /**
   * Space left below the pane. Pass 0 for a pane that paints its own
   * background to the bottom edge, where a gutter reads as a stray gap rather
   * than as breathing room.
   */
  bottomGutter: number = DEFAULT_BOTTOM_GUTTER
) => {
  'use memo'

  const ref = useRef<HTMLDivElement | null>(null)
  const [height, setHeight] = useState<number | undefined>(undefined)

  const measure = useCallback(() => {
    const node = ref.current
    if (!node) {
      return
    }
    // getBoundingClientRect().top is VIEWPORT-relative, so it shrinks as the
    // page scrolls. Adding scrollY converts it to a document offset; subtracting
    // the current scroll then gives the distance from the top of the viewport as
    // it would be at scroll 0. Measuring the raw rect instead would size the
    // pane differently depending on where the user happened to be scrolled.
    const documentTop = node.getBoundingClientRect().top + window.scrollY
    const available = window.innerHeight - documentTop - bottomGutter
    // A collapsed or not-yet-laid-out node can produce a nonsensical value;
    // never render a pane too small to show a row.
    setHeight(Math.max(MIN_HEIGHT, available))
  }, [bottomGutter])

  useEffect(() => {
    measure()
    window.addEventListener('resize', measure)

    // The chrome above can change height without the window resizing — the
    // banner image loads, a long competition name wraps, the registration strip
    // appears. Observing our own element catches the resulting shift in our top
    // edge; observing body alone would miss changes that leave body's own box
    // unchanged.
    const observer =
      typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null
    if (observer) {
      if (ref.current?.parentElement) {
        observer.observe(ref.current.parentElement)
      }
      if (document.body) {
        observer.observe(document.body)
      }
    }

    return () => {
      window.removeEventListener('resize', measure)
      observer?.disconnect()
    }
  }, [measure])

  return { ref, height }
}
